import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';

import strings from '../../../../strings';
import { userSelector } from '../../../../redux/selectors';
import { setUser } from '../../../../actions/userActions';
import { trackException } from '../../../../helpers/errorTracker';
import { useSignUp } from '../../../../hooks/useSignUp';
import { useUpdateUserMutation } from '../../../../services/user';
import { legalDocumentsVersion } from '../../../../settings/legalDocumentsVersion';

export const useProfile = ({ sharedList }) => {
  const [updateUser, { isLoading: isLoadingUpdateUser }] = useUpdateUserMutation();
  const { signUp, isLoading: isLoadingSignUp } = useSignUp();
  const user = useSelector(userSelector);
  const dispatch = useDispatch();
  const history = useHistory();

  const initialValues = {
    avatarUrl: user.avatarUrl || '',
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    ...(!user.email ? { email: '', password: '' } : undefined),
    termsAccepted: false,
  };
  const formSchema = Yup.object().shape({
    firstName: Yup.string().label('First name').required(),
    lastName: Yup.string().label('Last name').required(),
    ...(!user.email ? { email: Yup.string().email().required() } : undefined),
    ...(!user.email ? { password: Yup.string().min(8, strings.validation.passwordLength).required() } : undefined),
    termsAccepted: Yup.boolean().oneOf([true], strings.validation.termsAccepted),
  });

  const onUpdateUser = ({ avatarUrl, firstName, lastName, email }) =>
    updateUser({
      avatarUrl,
      firstName,
      lastName,
      email,
      privacyPolicyVersion: legalDocumentsVersion.privacyPolicyVersion,
      termsPolicyVersion: legalDocumentsVersion.termsPolicyVersion,
    })
      .then((response) => {
        dispatch(
          setUser({
            avatarUrl,
            firstName,
            lastName,
            onboardingCompleted: true,
            allyingCollections: [sharedList.collection],
          }),
        );
        history.push(`/shared-company-lists/${sharedList.slug}/install-extension`);

        return response;
      })
      .catch((error) => {
        trackException(error);

        throw error;
      });

  const onSignUp = ({ avatarUrl, firstName, lastName, email, password }, setStatus) =>
    signUp({
      collectionId: sharedList?.collection?.id,
      sharedListId: sharedList?.id,
      avatarUrl,
      firstName,
      lastName,
      email,
      password,
      role: 'ally',
      privacyPolicyVersion: legalDocumentsVersion.privacyPolicyVersion,
      termsPolicyVersion: legalDocumentsVersion.termsPolicyVersion,
    })
      .then(() => {
        history.push(`/shared-company-lists/${sharedList.slug}/install-extension`);
      })
      .catch((error) => {
        setStatus({ error: error?.data?.error });
      });

  const onSubmit = (userData, { setStatus }) => (user?.email ? onUpdateUser(userData) : onSignUp(userData, setStatus));

  return { user, initialValues, formSchema, isLoading: isLoadingUpdateUser || isLoadingSignUp, onSubmit };
};
